import React, { useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Divider,
  Alert,
} from '@mui/material';
import { Logout, Person, Factory, Build, CheckCircle, MenuBook, Inventory } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
  fetchAssignedWorkOrders,
  loadOperationContext,
  logoutOperator,
  selectWorkOrder,
  WorkOrderOperation,
} from '../../store/slices/operatorSlice';

import OperatorLogin from './components/OperatorLogin';
import WorkOrderQueue from './components/WorkOrderQueue';
import ActiveOperation from './components/ActiveOperation';
import MaterialConsumption from './components/MaterialConsumption';
import QualityCheck from './components/QualityCheck';
import WorkInstructions from './components/WorkInstructions';

const OperatorWorkstation: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentOperator, activeOperation, assignedWorkOrders } = useSelector(
    (state: RootState) => state.operator
  );

  const [selectedTab, setSelectedTab] = React.useState(0);

  // Fetch work orders once operator logs in
  useEffect(() => {
    if (currentOperator) {
      dispatch(fetchAssignedWorkOrders(currentOperator.workCenterCode));
    }
  }, [currentOperator, dispatch]);

  const handleSelectWorkOrder = (wo: WorkOrderOperation) => {
    dispatch(selectWorkOrder(wo));
    dispatch(loadOperationContext(wo));
    setSelectedTab(0); // Switch to Operation tab
  };

  const handleLogout = () => {
    dispatch(logoutOperator());
  };

  // If no operator logged in, show login screen
  if (!currentOperator) {
    return <OperatorLogin />;
  }

  const completedToday = assignedWorkOrders.filter((wo) => wo.status === 'completed').length;
  const inProgress = assignedWorkOrders.filter((wo) => wo.status === 'started' || wo.status === 'paused').length;

  return (
    <Box>
      {/* Operator Header Bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
        elevation={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <Person />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {currentOperator.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              ID: {currentOperator.employeeId} | {currentOperator.workCenterName} | {currentOperator.department}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <Box display="flex" gap={1}>
            <Chip
              icon={<Factory />}
              label={`${inProgress} In Progress`}
              color="warning"
              size="small"
            />
            <Chip
              icon={<CheckCircle />}
              label={`${completedToday} Completed Today`}
              color="success"
              size="small"
            />
          </Box>
          <Divider orientation="vertical" flexItem />
          <Button
            startIcon={<Logout />}
            onClick={handleLogout}
            color="inherit"
            variant="outlined"
            size="small"
          >
            Log Out
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column: Work Order Queue */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Assigned Queue
          </Typography>
          <WorkOrderQueue onSelectWorkOrder={handleSelectWorkOrder} />
        </Grid>

        {/* Right Column: Active Operation Tabs */}
        <Grid item xs={12} md={8}>
          {!activeOperation && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Select a work order from the queue on the left to begin working.
            </Alert>
          )}

          <Paper sx={{ mb: 1 }}>
            <Tabs
              value={selectedTab}
              onChange={(_, v) => setSelectedTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<Build />} iconPosition="start" label="Operation" />
              <Tab icon={<MenuBook />} iconPosition="start" label="Instructions" />
              <Tab icon={<Inventory />} iconPosition="start" label="Materials" />
              <Tab icon={<CheckCircle />} iconPosition="start" label="Quality" />
            </Tabs>
          </Paper>

          {selectedTab === 0 && <ActiveOperation />}
          {selectedTab === 1 && <WorkInstructions />}
          {selectedTab === 2 && <MaterialConsumption />}
          {selectedTab === 3 && <QualityCheck />}
        </Grid>
      </Grid>
    </Box>
  );
};

export default OperatorWorkstation;
